/**
 * PhoneVerify — Modal xác thực SĐT để nhận free credits
 * Mock OTP flow (replace bằng eSMS/SpeedSMS khi production)
 */

import React, { useState } from 'react';
import { Phone, Gift, CheckCircle, X, Loader2 } from 'lucide-react';
import { formatCredits, FREE_CREDITS } from '../../services/mock/creditMock';

interface PhoneVerifyProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
}

const PhoneVerify: React.FC<PhoneVerifyProps> = ({ isOpen, onClose, onVerified }) => {
  const [step, setStep] = useState<'phone' | 'otp' | 'success'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSendOtp = async () => {
    if (!phone || phone.length < 9) {
      setError('Vui lòng nhập số điện thoại hợp lệ');
      return;
    }
    setLoading(true);
    setError('');
    // TODO: Replace with real SMS API call
    // await fetch('/api/send-otp', { method: 'POST', body: JSON.stringify({ phone }) });
    await new Promise(r => setTimeout(r, 1500)); // Mock delay
    setLoading(false);
    setStep('otp');
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setError('Vui lòng nhập mã OTP');
      return;
    }
    setLoading(true);
    setError('');
    // TODO: Replace with real OTP verification
    // const result = await fetch('/api/verify-otp', { method: 'POST', body: JSON.stringify({ phone, otp }) });
    await new Promise(r => setTimeout(r, 1000)); // Mock delay

    // Mock: any OTP works
    setLoading(false);
    setStep('success');
    onVerified();
  };

  const handleClose = () => {
    setStep('phone');
    setPhone('');
    setOtp('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-indigo-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-indigo-600" />
            <h3 className="font-bold text-slate-800">Nhận credits miễn phí</h3>
          </div>
          <button onClick={handleClose} className="p-1 rounded-lg hover:bg-white/50">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-6 py-5">
          {step === 'phone' && (
            <>
              <p className="text-sm text-slate-600 mb-4">
                Xác thực số điện thoại để nhận{' '}
                <span className="font-bold text-indigo-600">
                  {formatCredits(FREE_CREDITS.phoneVerify)} credits miễn phí
                </span>
              </p>

              <div className="mb-4">
                <label className="text-xs font-bold text-slate-500 mb-1 block">Số điện thoại</label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 bg-slate-100 rounded-lg text-sm text-slate-500 font-mono">+84</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setError(''); }}
                    placeholder="901234567"
                    className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    maxLength={10}
                    autoFocus
                  />
                </div>
              </div>

              {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

              <button
                onClick={handleSendOtp}
                disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                {loading ? 'Đang gửi...' : 'Gửi mã OTP'}
              </button>
            </>
          )}

          {step === 'otp' && (
            <>
              <p className="text-sm text-slate-600 mb-1">
                Mã OTP đã gửi đến <span className="font-bold">+84{phone}</span>
              </p>
              <p className="text-xs text-slate-400 mb-4">Nhập mã 6 số từ tin nhắn SMS</p>

              <input
                type="text"
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                placeholder="000000"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 mb-4"
                maxLength={6}
                autoFocus
              />

              {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {loading ? 'Đang xác thực...' : 'Xác nhận'}
              </button>

              <button
                onClick={() => setStep('phone')}
                className="w-full mt-2 py-2 text-xs text-slate-500 hover:text-indigo-600"
              >
                Đổi số điện thoại
              </button>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-1">Xác thực thành công!</h4>
              <p className="text-sm text-slate-500 mb-4">
                Bạn nhận được{' '}
                <span className="font-bold text-indigo-600">{formatCredits(FREE_CREDITS.phoneVerify)} credits</span>
                {' '}miễn phí
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all active:scale-95"
              >
                Bắt đầu dịch ngay
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneVerify;
